import { api } from "@/services/omnimizer-api-service";
import { omnimizerApi } from "./api";
import { deploymentState } from "./state";
import type { OmnimizerDeployment } from "./types";

const OMNIMIZER_PATH =
  (typeof window !== "undefined" && window.env?.REACT_APP_OMNIMIZER_PATH) ||
  "/omnimizer-api";

const deploymentPath = (id: string, action: string) =>
  `${OMNIMIZER_PATH}/deployments/${encodeURIComponent(id)}/${action}`;

export type GpuHolder = {
  model_id: string;
  name: string;
  gpu_count: number;
  status: string;
  current_step: string | null;
  sleep_capable: boolean;
};

/** 409 body returned by the API when the cluster has no free GPU (services/gpu_admission.py). */
export type GpuCapacityReport = {
  code: "gpu_capacity" | "gpu_held_by_others";
  detail: string;
  requested: number;
  available: number;
  capacity: number;
  holders: GpuHolder[];
};

export type LifecycleCommand = "start" | "stop" | "sleep" | "cancel" | "switch";

export const lifecycleApi = {
  stop: (id: string) => omnimizerApi.stop(id),
  resume: (id: string) => omnimizerApi.start(id),
  sleep: (id: string) => api.post(deploymentPath(id, "sleep")),
  wake: (id: string) => api.post(deploymentPath(id, "wake")),
  switchTo: (id: string) =>
    api.post<{ released: { model_id: string; name: string; action: string }[]; action: string }>(
      deploymentPath(id, "switch"),
    ),
};

export function sleepCapable(d: Pick<OmnimizerDeployment, "args">) {
  return d.args?.runtime === "dynamo-vllm" && !!d.args.dynamo?.sleep_mode?.enabled;
}

const START_STEPS = new Set(["CREATED", "INITIALIZING", "RESUMING", "WAKING", "RECOVERING", "SCHEDULING", "PREPARING", "DOWNLOADING", "LOADING", "DEPLOYING"]);
const TRANSITIONS = new Set(["PAUSING", "RESUMING", "SLEEPING", "WAKING"]);

/** i18n key suffix for an in-flight lifecycle step, e.g. "Waking up…". */
export function transitionStep(d: Pick<OmnimizerDeployment, "status" | "current_step">) {
  return d.status === "STARTED" && d.current_step && TRANSITIONS.has(d.current_step)
    ? d.current_step.toLowerCase()
    : null;
}

/** Commands offered for a deployment, primary first. Only Dynamo deployments are managed here. */
export function availableCommands(d: OmnimizerDeployment): LifecycleCommand[] {
  if (!d.is_active) return [];
  const state = deploymentState(d);
  if (state === "failed") return ["stop"];
  if (state === "ready") return sleepCapable(d) ? ["sleep", "stop"] : ["stop"];
  if (state === "sleeping") return ["start", "stop"];
  if (state === "paused") return ["start"];
  if (state === "inProgress" && d.args?.runtime === "dynamo-vllm" && (!d.current_step || START_STEPS.has(d.current_step))) return ["cancel"];
  return [];
}

export function isTransitional(d: OmnimizerDeployment) {
  return deploymentState(d) === "inProgress";
}

export function gpuCapacityError(error: unknown): GpuCapacityReport | null {
  const response = (error as { response?: { status?: number; data?: unknown } })?.response;
  const data = response?.data as Partial<GpuCapacityReport> & { detail?: unknown } | undefined;
  const body = data?.code ? data : data?.detail;
  if (!body || typeof body !== "object") return null;
  const report = body as GpuCapacityReport;
  return (report.code === "gpu_capacity" || report.code === "gpu_held_by_others") && Array.isArray(report.holders) ? report : null;
}

export function errorMessage(error: unknown) {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object" && "detail" in detail) return String((detail as { detail: unknown }).detail);
  return error instanceof Error ? error.message : String(error);
}

/** Start or wake: the API picks wake for a sleeping model and resume for a stopped one. */
export function runCommand(d: OmnimizerDeployment, command: Exclude<LifecycleCommand, "switch">) {
  if (command === "start") return deploymentState(d) === "sleeping" ? lifecycleApi.wake(d.model_id) : lifecycleApi.resume(d.model_id);
  if (command === "sleep") return lifecycleApi.sleep(d.model_id);
  return lifecycleApi.stop(d.model_id); // stop and cancel start are both a pause
}

/** Release GPUs before creating a deployment: sleep when possible, else stop. */
export async function releaseHolders(holders: GpuHolder[]) {
  for (const h of holders) {
    const running = h.status === "SUCCESS";
    await (h.sleep_capable && running ? lifecycleApi.sleep(h.model_id) : lifecycleApi.stop(h.model_id));
  }
}

/** Resolve once none of the given deployments holds GPUs any more. */
export async function waitReleased(
  ids: string[],
  list: () => Promise<OmnimizerDeployment[]>,
  { timeoutMs = 900_000, intervalMs = 1_500, signal }: { timeoutMs?: number; intervalMs?: number; signal?: AbortSignal } = {},
) {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    if (signal?.aborted) throw new DOMException("Cancelled", "AbortError");
    const rows = await list();
    if (signal?.aborted) throw new DOMException("Cancelled", "AbortError");
    const busy = rows.filter((d) => ids.includes(d.model_id) && ["ready", "inProgress"].includes(deploymentState(d)));
    const failed = rows.filter((d) => ids.includes(d.model_id) && deploymentState(d) === "failed");
    if (failed.length) throw new Error(failed.map((d) => `${d.name}: ${d.error || "failed"}`).join("; "));
    if (ids.some(id => !rows.some(d => d.model_id === id))) throw new Error("Holder stop was not confirmed");
    if (!busy.length) return;
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  throw new Error("Timed out waiting for the GPU to be released");
}


/** Shared labels used by the existing list and its pending-reason badge. */
export function lifecycleState(d: Pick<OmnimizerDeployment, "status" | "current_step">) {
  if (d.status === "FAILED") return "failed";
  if (d.current_step === "PAUSING") return "stopping";
  if (d.status === "PAUSED" && d.current_step === "ASLEEP") return "sleeping";
  if (d.status === "PAUSED" || d.current_step === "PAUSED") return "stopped";
  if (d.status === "SUCCESS" && d.current_step === "COMPLETED") return "running";
  return "starting";
}
export const gpuConflict = (error: unknown) => gpuCapacityError(error) || undefined;
export function switchHolders<T extends { gpu_count: number }>(conflict: { requested: number; available: number; holders: T[]; code?: string; detail?: string; capacity?: number }) {
  let needed = conflict.requested - conflict.available;
  return conflict.holders.filter(holder => { if (needed <= 0) return false; needed -= holder.gpu_count; return true; });
}
